import { useQuery } from '@tanstack/react-query';
import { mtmRunsService } from '@/services';
import { queryKeys } from '@/services/query-keys';
import type { HistoryFeedFilters, MtmRunBook, MtmRunItem } from '@/types';
import { bookKey, isHistoryItemNotFound, useHistoryPages } from './use-history-pages';

const runKey = (run: MtmRunItem): number => run.id;

/** GET /mtm-runs/?bucket=stock_lb: the Stock-L/B run log, newest-first, paged on the server. */
export const useStockLbRunPages = (
  filters: HistoryFeedFilters,
  { enabled = true, keepPrevious = false }: { enabled?: boolean; keepPrevious?: boolean } = {}
) =>
  useHistoryPages<MtmRunItem>({
    queryKey: queryKeys.trades.mtmRunPages(filters),
    fetchPage: (limit, offset) => mtmRunsService.getStockLbRunsPage({ ...filters, limit, offset }),
    keyOf: runKey,
    enabled,
    keepPrevious,
  });

/** GET /mtm-runs/by-counterparty/?bucket=stock_lb: one row per (counterparty, currency) book. */
export const useStockLbRunBooks = (filters: HistoryFeedFilters) =>
  useHistoryPages<MtmRunBook>({
    queryKey: queryKeys.trades.mtmRunBooks(filters),
    fetchPage: (limit, offset) => mtmRunsService.getStockLbBooksPage({ ...filters, limit, offset }),
    keyOf: bookKey,
    keepPrevious: true,
  });

/** GET /mtm-runs/{id}/: one run, for a deep link older than the loaded pages. */
export const useMtmRun = (runId: number | undefined) =>
  useQuery<MtmRunItem>({
    queryKey: queryKeys.trades.mtmRun(runId ?? 0),
    queryFn: () => mtmRunsService.getRun(runId as number),
    enabled: runId !== undefined,
    staleTime: 30 * 1000,
    retry: (failureCount, error) => !isHistoryItemNotFound(error) && failureCount < 1,
  });
